import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useSnackbar } from 'notistack';
import { csAdminApi } from '../../services/api';
import { buildDirectorFormData, formatAppointmentDate } from '../../utils/buildDirectorFormData';

// Import step components
import SelectCompanyStep from './steps/SelectCompanyStep';
import SelectSourceStep from './steps/SelectSourceStep';
import SearchPoolStep from './steps/SearchPoolStep';
import PersonalInfoStep from './steps/PersonalInfoStep';
import ContactInfoStep from './steps/ContactInfoStep';
import DocumentsStep, { validateDirectorDocumentsStep } from './steps/DocumentsStep';
import { useLookups } from '../../hooks/useLookups';

const AddDirector = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { items: directorDocumentTypes } = useLookups('DIRECTOR_DOCUMENT_TYPE');
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sourceType, setSourceType] = useState(''); // 'pool' or 'new'

  const [formData, setFormData] = useState({
    // Company (selected first)
    companyId: '',
    companyName: '',
    
    // Source
    sourceType: '',
    selectedDirectorId: null,
    selectedDirector: null, // Full director object from pool
    
    // Personal Info (for new director)
    firstName: '',
    lastName: '',
    emiratesId: '',
    passportNumber: '',
    nationality: '',
    dateOfBirth: null,
    gender: '',
    
    // Contact Info
    email: '',
    mobileNumber: '',
    address: '',
    
    // Assignment details
    position: '',
    appointmentDate: null,
    isActive: true,
    
    // Documents
    documents: [],
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const prefillCompanyId = searchParams.get('companyId');
    if (!prefillCompanyId || formData.companyId) return;

    csAdminApi.getCompanyById(prefillCompanyId)
      .then((res) => {
        const company = res.data?.data;
        if (!company) return;
        const name = company.company_name || company.name;
        setFormData((prev) => ({
          ...prev,
          companyId: prefillCompanyId,
          companyName: name || '',
        }));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Steps change based on source type
  // Pool directors don't need Documents step - they already have documents in the system
  const getSteps = () => {
    if (sourceType === 'new') {
      return ['Select Company', 'Select Source', 'Personal Info', 'Contact Info', 'Documents'];
    }
    // Default pool flow (also before a source is chosen — avoids premature "Save Director" on step 2)
    return ['Select Company', 'Select Source', 'Search Pool', 'Assignment Details'];
  };

  const steps = getSteps();

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0: // Select Company
        if (!formData.companyId) {
          newErrors.companyId = 'Please select a company';
        }
        break;
      
      case 1: // Select Source
        if (!sourceType) {
          newErrors.sourceType = 'Please select a source';
        }
        break;
      
      case 2: // Search Pool or Personal Info
        if (sourceType === 'new') {
          if (!formData.firstName) newErrors.firstName = 'First name is required';
          if (!formData.lastName) newErrors.lastName = 'Last name is required';
          if (!formData.emiratesId) newErrors.emiratesId = 'Emirates ID is required';
          if (!formData.passportNumber) newErrors.passportNumber = 'Passport number is required';
          if (!formData.nationality) newErrors.nationality = 'Nationality is required';
          if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
          if (!formData.gender) newErrors.gender = 'Gender is required';
        } else if (!formData.selectedDirectorId) {
          newErrors.selectedDirectorId = 'Please select a director from the pool';
        }
        break;

      case 3: // Assignment Details (pool) or Contact Info (new)
        if (sourceType === 'new') {
          if (!formData.email) {
            newErrors.email = 'Email is required';
          } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
          }
          if (!formData.mobileNumber) newErrors.mobileNumber = 'Mobile number is required';
        } else {
          if (!formData.position) newErrors.position = 'Position is required';
          if (!formData.appointmentDate) newErrors.appointmentDate = 'Appointment date is required';
        }
        break;

      case 4: {
        const docError = validateDirectorDocumentsStep(formData, directorDocumentTypes);
        if (docError) newErrors.documents = docError;
        break;
      }

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) return;

    try {
      setLoading(true);

      if (sourceType === 'pool') {
        const repDirectorId = formData.selectedDirector?.directorId
          ?? formData.selectedDirector?.representative_director_id
          ?? formData.selectedDirector?.id;
        
        // Validate that we have a valid director ID
        if (!repDirectorId || isNaN(Number(repDirectorId))) {
          throw new Error('Invalid director selection. Please select a director from the pool.');
        }
        
        // Validate required fields
        if (!formData.companyId) {
          throw new Error('Company ID is missing. Please select a company.');
        }
        
        if (!formData.appointmentDate && !formatAppointmentDate(formData.appointmentDate)) {
          throw new Error('Appointment date is required.');
        }
        
        await csAdminApi.assignDirectorFromPool(repDirectorId, {
          company_id: formData.companyId,
          person_id: formData.selectedDirector?.personId ?? formData.selectedDirector?.person_id,
          position_title: formData.position || 'Director',
          start_date: formatAppointmentDate(formData.appointmentDate) || new Date().toISOString().slice(0, 10),
        });
      } else {
        const fd = buildDirectorFormData(formData);
        const res = await csAdminApi.createDirector(fd);
        
        // Check if the response indicates success (201 Created or 200 OK)
        if (res.status !== 201 && res.status !== 200) {
          throw new Error(res.data?.error?.message || res.data?.message || 'Create failed');
        }
        
        // Additional check: if response has explicit success flag, verify it
        if (res.data?.success === false) {
          throw new Error(res.data?.error?.message || res.data?.message || 'Create failed');
        }
      }

      enqueueSnackbar('Director added successfully!', { variant: 'success' });
      if (sourceType === 'pool' && formData.companyId) {
        navigate(`/companies/${formData.companyId}`);
      } else {
        navigate('/directors');
      }
    } catch (err) {
      console.error('Error creating director:', err);
      const msg =
        err.response?.data?.error?.message ||
        err.response?.data?.error?.details ||
        err.message ||
        'Failed to add director';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSource = (type) => {
    setSourceType(type);
    setFormData((prev) => ({
      ...prev,
      sourceType: type,
      selectedDirectorId: null,
      selectedDirector: null,
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.sourceType;
      return next;
    });
  };

  const handleSelectCompany = (companyId, companyName) => {
    setFormData({ ...formData, companyId, companyName });
  };

  const handleSelectDirector = (director) => {
    if (!director) {
      setFormData({
        ...formData,
        selectedDirectorId: null,
        selectedDirector: null,
      });
      return;
    }
    setFormData({
      ...formData,
      selectedDirectorId: director.personId ?? director.person_id ?? director.id,
      selectedDirector: director,
    });
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <SelectCompanyStep
            selectedCompanyId={formData.companyId}
            onSelectCompany={handleSelectCompany}
            errors={errors}
          />
        );
      case 1:
        return (
          <SelectSourceStep
            sourceType={sourceType}
            onSelectSource={handleSelectSource}
            companyName={formData.companyName}
            errors={errors}
          />
        );
      case 2:
        return sourceType === 'new' ? (
          <PersonalInfoStep
            formData={formData}
            onChange={setFormData}
            errors={errors}
          />
        ) : (
          <SearchPoolStep
            selectedDirectorId={formData.selectedDirectorId}
            onSelectDirector={handleSelectDirector}
            excludeCompanyId={formData.companyId}
            companyName={formData.companyName}
            errors={errors}
          />
        );
      case 3:
        return sourceType === 'new' ? (
          <ContactInfoStep
            formData={formData}
            onChange={setFormData}
            errors={errors}
          />
        ) : (
          <AssignmentDetailsStep
            formData={formData}
            onChange={setFormData}
            errors={errors}
          />
        );
      case 4:
        return (
          <DocumentsStep
            formData={formData}
            onChange={setFormData}
            errors={errors}
          />
        );
      default:
        return 'Unknown step';
    }
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return !!formData.companyId;
      case 1:
        return !!sourceType;
      default:
        return true;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 64px)' }}>
      {/* Scrollable Content Area */}
      <Box sx={{ flex: 1, px: 3, py: 3, pb: 12 }}>
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Add New Director
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Add a director from the pool or create a new entry
          </Typography>
        </Box>

        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step Content */}
        <Paper sx={{ p: 4 }}>
          {getStepContent(activeStep)}
        </Paper>
      </Box>

      {/* Persistent Bottom Navigation */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 240, // Account for sidebar width
          right: 0,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          px: 3,
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 1100,
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
        }}
      >
        <Button
          variant="outlined"
          onClick={() => navigate('/directors')}
        >
          Cancel
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Previous
          </Button>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading || (activeStep === 1 && !sourceType)}
            >
              {loading ? <CircularProgress size={24} /> : 'Save Director'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!canProceed()}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Simple Assignment Details step for pool directors (final step)
const AssignmentDetailsStep = ({ formData, onChange, errors }) => {
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Assignment Details
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Configure the director's role in <strong>{formData.companyName}</strong>
      </Typography>

      {formData.selectedDirector && (
        <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: 1, borderColor: 'primary.200' }}>
          <Typography variant="body2" fontWeight={600} color="primary.main" sx={{ mb: 0.5 }}>
            Selected Director
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {formData.selectedDirector.fullName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formData.selectedDirector.position} • Emirates ID: {formData.selectedDirector.emiratesId}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Currently at: {formData.selectedDirector.currentCompany}
          </Typography>
        </Box>
      )}

      {/* Info note about documents */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'info.50', borderRadius: 1, border: 1, borderColor: 'info.200' }}>
        <Typography variant="body2" color="info.main">
          Documents for this director are already on file and will be linked automatically.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <FormControl required fullWidth error={!!errors.position}>
          <InputLabel>Position in {formData.companyName}</InputLabel>
          <Select
            value={formData.position}
            label={`Position in ${formData.companyName}`}
            onChange={(e) => onChange({ ...formData, position: e.target.value })}
          >
            <MenuItem value="">Select position</MenuItem>
            <MenuItem value="Board Director">Board Director</MenuItem>
            <MenuItem value="Executive Director">Executive Director</MenuItem>
            <MenuItem value="Non-Executive Director">Non-Executive Director</MenuItem>
            <MenuItem value="Independent Director">Independent Director</MenuItem>
            <MenuItem value="Chairman">Chairman</MenuItem>
            <MenuItem value="Vice Chairman">Vice Chairman</MenuItem>
          </Select>
          {errors.position && <FormHelperText>{errors.position}</FormHelperText>}
        </FormControl>

        <DatePicker
          label="Appointment Date *"
          value={formData.appointmentDate}
          onChange={(value) => onChange({ ...formData, appointmentDate: value })}
          inputFormat="dd/MM/yyyy"
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              required
              error={!!errors.appointmentDate}
              helperText={errors.appointmentDate}
            />
          )}
        />
      </Box>
    </Box>
  );
};

export default AddDirector;
